/**
 * CSSReveal – Fade-In-Effekt beim ersten Sichtbarwerden.
 *
 * WARUM diese Komponente?
 *   Framer-Motion registriert auf jedem `motion.*`-Element Pointer-Handler mit
 *   `passive: false`. Auf iOS Safari „verschluckt" das den Body-Scroll, wenn der
 *   Finger kurz ruht und dann neu ansetzt. Wir brauchen also einen Reveal-Effekt
 *   OHNE Touch-Handler.
 *
 * WIE:
 *   - Nativer `IntersectionObserver` (kein DOM-Listener, kein Pointer-Event).
 *   - Beim ersten Sichtbarwerden setzen wir `data-revealed="true"`. CSS-Transitions
 *     erledigen den Rest – reine GPU-Transformation, kein JS pro Frame.
 *   - `once: true` – beobachten wird nach dem ersten Trigger disconnect'et.
 *
 * Props:
 *   as       – gerendertes Element (`"div"` per default, kann `"section"`, `"article"` etc.)
 *   delay    – Verzögerung in ms für Stagger (index * 80ms sinnvoll)
 *   className – Tailwind-Klassen wie gewohnt
 *   children – Content
 *   ...rest  – wird an das Element durchgereicht (`data-testid`, `id`, `style`, `onClick` …)
 */
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

export default function CSSReveal({
  as: Tag = "div",
  delay = 0,
  className = "",
  style,
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const reducedMotion = useReducedMotion();
  const visible = revealed || reducedMotion;
  const stagger = Math.min(Math.max(delay, 0), 180);

  useEffect(() => {
    // Fallback für alte Browser: sofort einblenden.
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px -80px 0px", threshold: 0.01 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      data-public-motion-owned=""
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : "translate3d(0,22px,0)",
        transition: reducedMotion ? "none" : `opacity 0.85s ease-out ${stagger}ms, transform 1.1s cubic-bezier(0.16,1,0.3,1) ${stagger}ms`,
        willChange: visible ? "auto" : "opacity, transform",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
